import DiagnosticsModule, {
  MetricNames,
  MetricTags,
  OperationNames,
} from '@modules/DiagnosticsModule';

// Marks older than this were abandoned without being cleared.
const STALE_MARK_MS = 10 * 60 * 1000;

interface AnswerMark {
  // Monotonic, for the latency sample.
  startedPerfMs: number;
  // Wall clock, for staleness: performance.now() can pause while the OS sleeps.
  startedWallMs: number;
}

const answerMarks = new Map<string, AnswerMark>();

/** True when the wall clock is more than STALE_MARK_MS away from the mark, in either direction. */
function isStale(mark: AnswerMark, wallNow: number): boolean {
  return Math.abs(wallNow - mark.startedWallMs) > STALE_MARK_MS;
}

/** Notes when this app asked the softphone to answer a call. */
export function markAnswerRequested(callId: string): void {
  const wallNow = Date.now();
  for (const [id, mark] of answerMarks) {
    if (isStale(mark, wallNow)) {
      answerMarks.delete(id);
    }
  }
  // A repeat request (second click, or Accept from another control) keeps the
  // first mark so latency covers the whole wait the agent saw.
  if (!answerMarks.has(callId)) {
    answerMarks.set(callId, {
      startedPerfMs: performance.now(),
      startedWallMs: wallNow,
    });
  }
}

/** Forgets the answer mark, e.g. when the agent rejects the call. */
export function clearAnswerRequested(callId: string): void {
  answerMarks.delete(callId);
}

/**
 * Records call-accept latency, measured from the first answer request for
 * the call to established, if this app requested the answer.
 */
export function recordCallAccepted(callId: string): void {
  const mark = answerMarks.get(callId);
  if (!mark) {
    return;
  }
  answerMarks.delete(callId);
  if (isStale(mark, Date.now())) {
    return;
  }
  DiagnosticsModule.recordHistogram(
    MetricNames.LATENCY_DURATION,
    Math.round(performance.now() - mark.startedPerfMs),
    { [MetricTags.OPERATION_NAME]: OperationNames.CALL_ACCEPT },
  );
}

export function __resetCallAcceptLatencyForTests(): void {
  answerMarks.clear();
}
